import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './hooks/useAuth';
import { AppShell } from './components/layout/AppShell';
import { DashboardPage } from './pages/DashboardPage';
import { CompaniesPage } from './pages/CompaniesPage';
import { CompanyDetailPage } from './pages/CompanyDetailPage';
import { ContactsPage } from './pages/ContactsPage';
import { SaleMandatesPage } from './pages/SaleMandatesPage';
import { SaleMandateDetailPage } from './pages/SaleMandateDetailPage';
import { SettingsPage } from './pages/SettingsPage';
import { CompanySettingsPage } from './pages/CompanySettingsPage';
import { CommunicationPage } from './pages/CommunicationPage';
import { RepliesPage } from './pages/RepliesPage';
import { DuplicatesPage } from './pages/DuplicatesPage';
import { ContactDetailPage } from './pages/ContactDetailPage';
import { JobsPage } from './pages/JobsPage';
import { ReviewPage } from './pages/ReviewPage';
import LoginPage from './pages/LoginPage';
import UsersPage from './pages/UsersPage';
import './styles/global.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function ProtectedRoutes() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;

  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/companies" element={<CompaniesPage />} />
        <Route path="/companies/:id" element={<CompanyDetailPage />} />
        <Route path="/duplikate" element={<DuplicatesPage />} />
        <Route path="/contacts" element={<ContactsPage />} />
        <Route path="/contacts/:id" element={<ContactDetailPage />} />
        <Route path="/verkaufsmandate" element={<SaleMandatesPage />} />
        <Route path="/verkaufsmandate/:id" element={<SaleMandateDetailPage />} />
        <Route path="/unternehmen" element={<CompanySettingsPage />} />
        {/* Neue Kommunikations-Baumansicht */}
        <Route path="/kommunikation" element={<RepliesPage />} />
        {/* Alter /antworten-Pfad → Redirect auf /kommunikation */}
        <Route path="/antworten" element={<Navigate to="/kommunikation" replace />} />
        {/* E-Mail-/SMTP-Settings (vormals /kommunikation) unter Einstellungen */}
        <Route path="/mail-einstellungen" element={<CommunicationPage />} />
        <Route path="/einstellungen" element={<SettingsPage />} />
        <Route path="/jobs" element={<JobsPage />} />
        <Route path="/review" element={<ReviewPage />} />
        <Route path="/benutzer" element={<UsersPage />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </AppShell>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginGate />} />
            <Route path="/*" element={<ProtectedRoutes />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}

// Wenn bereits eingeloggt → direkt zum Dashboard
function LoginGate() {
  const { user } = useAuth();
  if (user) return <Navigate to="/dashboard" replace />;
  return <LoginPage />;
}

export default App;
